import type { CSSProperties, ReactNode, SyntheticEvent } from "react";
import MuiAlert, { type AlertColor } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

// Thin wrapper mapping antd <Alert> API onto MUI Alert so call sites swap by
// import only. antd uses message/description/type; MUI uses children/severity.
// message + description -> AlertTitle(message) + description body.

type AntdAlertType = "success" | "info" | "warning" | "error";

export interface AlertProps {
  message?: ReactNode;
  description?: ReactNode;
  type?: AntdAlertType;
  showIcon?: boolean;
  closable?: boolean;
  onClose?: (e: SyntheticEvent) => void;
  action?: ReactNode;
  icon?: ReactNode;
  banner?: boolean;
  style?: CSSProperties;
  className?: string;
  /** allow MUI escape hatches / already-MUI call sites */
  severity?: AlertColor;
  children?: ReactNode;
  sx?: Record<string, unknown>;
  "data-cy"?: string;
  "data-testid"?: string;
}

export const Alert = ({
  message,
  description,
  type,
  showIcon = true,
  closable,
  onClose,
  action,
  icon,
  banner,
  style,
  className,
  severity,
  children,
  sx,
  "data-cy": dataCy,
  "data-testid": dataTestid,
}: AlertProps) => {
  const sev: AlertColor = severity ?? type ?? "info";
  const body =
    children ??
    (description ? (
      <>
        <AlertTitle sx={{ mb: 0.5 }}>{message}</AlertTitle>
        {description}
      </>
    ) : (
      message
    ));

  return (
    <MuiAlert
      severity={sev}
      variant={banner ? "standard" : "outlined"}
      onClose={closable ? (onClose as (e: SyntheticEvent) => void) : undefined}
      action={action}
      icon={showIcon ? icon : false}
      style={style}
      className={className}
      sx={sx}
      data-cy={dataCy}
      data-testid={dataTestid}
    >
      {body}
    </MuiAlert>
  );
};

export default Alert;
